/**
 * Command palette (⌘K / Ctrl+K).
 *
 * Combines global search across all three entities with navigation and quick actions.
 * For an internal tool where staff live in one screen all day, this is the single
 * highest-leverage interaction: it removes the sidebar → list → filter → find loop and
 * replaces it with typing a client's name.
 */

import * as React from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, ShieldAlert, Users } from 'lucide-react';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useGlobalSearch } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { entityRoute, NAV_ITEMS } from '@/lib/registry/navigation';
import { cn } from '@/lib/utils';

const ENTITY_ICONS = { client: Users, policy: FileText, claim: ShieldAlert } as const;

/** Group eyebrow, declared once — three call sites previously repeated it verbatim. */
const GROUP_HEADING = [
  '[&_[cmdk-group-heading]]:text-muted-soft [&_[cmdk-group-heading]]:px-2',
  '[&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5',
  '[&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold',
  '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em]',
  'first:[&_[cmdk-group-heading]]:pt-1',
].join(' ');

/** A palette row: 8px radius, neutral selected wash, icon held back until selected. */
const ITEM = [
  'flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-[13.5px]',
  'transition-colors duration-150',
  'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
  '[&_svg]:opacity-65 data-[selected=true]:[&_svg]:opacity-100 [&_svg]:transition-opacity',
].join(' ');

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [term, setTerm] = React.useState('');

  // The query only runs once the palette is open and the term is meaningful, so
  // opening the palette costs nothing.
  const { data, isFetching } = useGlobalSearch(term, open);

  React.useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const navItems = NAV_ITEMS.filter((item) => !item.permission || can(item.permission));

  const groups = [
    { key: 'clients' as const, labelKey: 'search.groupClients', hits: data?.clients ?? [] },
    { key: 'policies' as const, labelKey: 'search.groupPolicies', hits: data?.policies ?? [] },
    { key: 'claims' as const, labelKey: 'search.groupClaims', hits: data?.claims ?? [] },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        showCloseButton={false}
        className="top-[15%] translate-y-0 gap-0 overflow-hidden p-0"
        aria-label={t('search.placeholder')}
      >
        {/* `shouldFilter={false}` — results come pre-ranked from Postgres full-text
            search; letting cmdk re-filter them client-side would fight that ranking. */}
        <Command shouldFilter={false} loop>
          <div className="border-border flex items-center gap-3 border-b px-4">
            <Search className="text-muted-soft size-4 shrink-0" />
            <Command.Input
              value={term}
              onValueChange={setTerm}
              placeholder={t('search.placeholder')}
              className="placeholder:text-muted-soft h-[52px] w-full bg-transparent text-[15px] outline-none"
            />
            {isFetching && (
              <span className="border-primary size-3.5 shrink-0 animate-spin rounded-full border-2 border-t-transparent" />
            )}
          </div>

          <Command.List className="max-h-[22rem] overflow-x-hidden overflow-y-auto p-2">
            {term.trim().length >= 2 && !isFetching && (data?.total ?? 0) === 0 && (
              <div className="text-muted-foreground py-12 text-center text-sm">{t('search.noResults')}</div>
            )}

            {term.trim().length < 2 && (
              <p className="text-muted-soft px-2 py-3 text-xs">{t('search.commandHint')}</p>
            )}

            {groups.map((group) =>
              group.hits.length ? (
                <Command.Group key={group.key} heading={t(group.labelKey)} className={GROUP_HEADING}>
                  {group.hits.map((hit) => {
                    const Icon = ENTITY_ICONS[hit.entity];
                    return (
                      <Command.Item
                        key={`${hit.entity}-${hit.id}`}
                        value={`${hit.entity}-${hit.id}`}
                        onSelect={() => go(entityRoute(hit.entity, hit.id))}
                        className={ITEM}
                      >
                        <Icon className="text-muted-foreground size-4 shrink-0" />
                        <span className="truncate font-medium">{hit.title}</span>
                        <span className="text-muted-soft ml-auto shrink-0 truncate pl-3 text-xs">
                          {hit.subtitle}
                        </span>
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ) : null,
            )}

            <Command.Group heading={t('search.groupNavigation')} className={GROUP_HEADING}>
              {navItems
                .filter((item) => !term || t(item.labelKey).toLowerCase().includes(term.toLowerCase()))
                .map((item) => (
                  <Command.Item
                    key={item.to}
                    value={`nav-${item.to}`}
                    onSelect={() => go(item.to)}
                    className={ITEM}
                  >
                    <item.icon className="text-muted-foreground size-4 shrink-0" />
                    <span>{t(item.labelKey)}</span>
                  </Command.Item>
                ))}
            </Command.Group>
          </Command.List>

          <div className="text-muted-soft border-border flex items-center justify-between border-t px-3.5 py-2.5 text-[11px]">
            <span className="flex items-center gap-1.5">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> {t('search.hintNavigate')}
              <Kbd className="ml-2.5">↵</Kbd> {t('search.hintOpen')}
            </span>
            <Kbd>Esc</Kbd>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Keycap. Sized to the glyph rather than to a fixed box so "⌘" and "Esc" both sit
 * correctly, and set in the UI face — a monospace keycap next to Inter reads as code.
 */
function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'bg-foreground/[0.055] text-muted-foreground dark:bg-foreground/[0.09]',
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[5px] px-1',
        'font-sans text-[10.5px] leading-none font-medium',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export { Kbd };

/** Registers the ⌘K / Ctrl+K shortcut and owns the palette's open state. */
export function useCommandPalette() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
      // "/" is the other muscle memory for search, but must not hijack typing.
      if (event.key === '/' && !isTypingTarget(event.target)) {
        event.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return { open, setOpen };
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}
